import { BaseComponent, Component, Prop, Ref, SimpleEvent, Watch } from "@eastsideco/salvo-ts";
import { ThemeComponent } from "@/theme/ThemeComponent";
import { SubscriptionForm } from '../SubscriptionForm/SubscriptionForm';

/**
 * Linked Subscriptions
 *
 * This component exclusively handles recharge subscription options on
 * product pages.
 *
 * Functionality around checking URL params is included, but
 * could potentially be moved in to liquid so as not to switch
 * the default selected recharge option after page load
 *
 */
@Component
export class LinkedSubscriptions extends ThemeComponent {

    private subscriptionChoices: NodeListOf<HTMLInputElement>;
    private addToCartPriceElement: NodeListOf<HTMLInputElement> | null;
    private formInputUnitType: HTMLInputElement | null;
    private formInputFrequency: HTMLInputElement | null;
    private addToCartForm: HTMLFormElement | null;
    private addToCartSubmit: NodeListOf<HTMLInputElement> | null;
    private fromPriceElement: HTMLButtonElement | null;
    private productMain: HTMLElement | null;

    constructor() {
        super();
        this.subscriptionChoices = this.querySelectorAll('.product-form__subscription-choice');
        this.addToCartPriceElement = document.querySelectorAll('.product-form__selected-price');
        this.formInputUnitType = this.querySelector('#rc_shipping_interval_unit_type');
        this.formInputFrequency = this.querySelector('#rc_shipping_interval_frequency');
        this.addToCartForm = document.querySelector('.product-form');
        this.addToCartSubmit = document.querySelectorAll('.product-form__submit');
        this.fromPriceElement = document.querySelector('.product-main__details__meta');
        this.productMain = document.querySelector('.product-main__wrapper');
    }

    async init() {

        const subscriptionForm: SubscriptionForm =
        document.querySelector('.subscription-form') as SubscriptionForm;

        if (this.subscriptionChoices) {
            for (const choice of this.subscriptionChoices) {
                choice.addEventListener('change', (event: Event) => {
                    subscriptionForm.updateSelectedFrequency(choice.id);
                    const hasGift = choice.dataset.hasGift;
                    this.setGift(String(hasGift));
                });
            }
        }
        this.$subscribe(subscriptionForm.onProductChange, (frequency: string) => {
            void this.setSelectedFrequency(frequency);
        });
        // Update the selected frequency on page load
        if (this.productMain) {
            const selectedRadio =
                this.productMain.querySelector('.radio-selection--checked input[type=radio]') as HTMLInputElement;
            await this.handleChargeDelay(selectedRadio);
        }

        this.handleButtonAttributes();
    }
    setGift(giftCheck:string) {
        if (this.addToCartSubmit) {
            for (const button of this.addToCartSubmit) {
                button.setAttribute('has_gift', giftCheck);
            }
        }
    }
    async setSelectedFrequency(frequency: string) {
        // Get the selected frequency radio
        const frequencyInput = this.querySelector(`#${frequency}`) as HTMLInputElement;
        // Set values on the cart submit button
        try {
            if (this.addToCartSubmit && frequency == 'one-time') {
                this.formInputFrequency?.setAttribute('value', '');
                for (const button of this.addToCartSubmit) {
                    button?.setAttribute('frequency', '');
                    button?.setAttribute('is_subscription', 'false');
                    button?.setAttribute('quantity', '1');
                    button?.setAttribute('is_subscription', 'false');
                    if (frequencyInput?.dataset.sellingPlan) {
                        button?.setAttribute('selling_plans', frequencyInput?.dataset.sellingPlan);
                    }
                }

            } else if (
                frequencyInput?.dataset.sellingPlan &&
                frequencyInput?.dataset.quantity
            ) {
                if (this.addToCartSubmit) {
                    // Update the add to cart button with properties on the selected radio option
                    const frequency = frequencyInput?.id.split('-').pop() || '';
                    const frequencyUnit = frequencyInput?.getAttribute('data-frequency-unit') || 'days';
                    this.formInputFrequency?.setAttribute('value', frequency);

                    if(this.formInputUnitType) {
                        this.formInputUnitType.value = "days";
                    }

                    for (const button of this.addToCartSubmit) {
                        button?.setAttribute('is_subscription', 'true');
                        button?.setAttribute('quantity', frequencyInput?.dataset.quantity);
                        button?.setAttribute('selling_plan', frequencyInput?.dataset.sellingPlan);
                        button?.setAttribute('frequency', frequency);
                        button?.setAttribute('unit_type', frequencyUnit);
                    }
                }
            }
            // Adds the selected product upsell id, if present, to the submit button
            if (this.addToCartSubmit && frequencyInput?.dataset.upsellId && frequencyInput?.dataset.upsellId != '') {
                for (const button of this.addToCartSubmit) {
                    button.setAttribute('upsell_id', frequencyInput?.dataset.upsellId);
                }
            }
            // Deselect all radios and select the
            // applicable radio
            if (this.subscriptionChoices) {
                for (const choice of this.subscriptionChoices) {
                    const radioParent = choice.closest('.radio-selection');
                    if (radioParent) {
                        if (choice.id == frequency) {
                            choice.checked = true;
                            radioParent.classList.remove('radio-selection--checked');
                        } else {
                            choice.checked = false;
                            radioParent.classList.remove('radio-selection--checked');
                        }
                    }
                }
            }
            if (this.formInputUnitType === null) {
                this.formInputUnitType = document.createElement('input');
                this.formInputUnitType.setAttribute('type', 'hidden');
                this.formInputUnitType.setAttribute('name', "properties[shipping_interval_unit_type]");
                this.formInputUnitType.value = frequencyInput?.getAttribute('data-frequency-unit') || 'days';
                this.addToCartForm?.prepend(this.formInputUnitType);
            }
            if (this.formInputFrequency === null) {
                this.formInputFrequency = document.createElement('input');
                this.formInputFrequency.setAttribute('type', 'hidden');
                this.formInputFrequency.setAttribute('name', "properties[shipping_interval_frequency]");
                this.addToCartForm?.prepend(this.formInputFrequency);
            }
            const choiceWrapper = frequencyInput?.closest('.radio-selection');
            if (choiceWrapper) {
                await this.handleChargeDelay(frequencyInput as HTMLInputElement);
                choiceWrapper.classList.add('radio-selection--checked');
                const selectedPrice = frequencyInput?.getAttribute('data-price');
                if (this.addToCartPriceElement && selectedPrice) {
                    for (const priceItem of this.addToCartPriceElement) {
                        priceItem.innerText = selectedPrice;
                    }
                }
            }
        } catch(error) {
            throw new Error();
        }
    }
    protected async checkCartHasSubscription() {
        const response = await fetch('/cart.js', {
            method: 'GET',
            headers: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'Content-Type': 'application/json'
            }
        });
        const cartData = await response.json();
        let hasSubscription = false;
        if (cartData.items.length > 0) {
            for (const product of cartData.items) {
                const properties = product.properties;
                if (properties.shipping_interval_frequency && properties.shipping_interval_unit_type) {
                    hasSubscription = true;
                    break;
                }
            }
        }
        return hasSubscription;
    }
    async handleChargeDelay(option: HTMLInputElement) {
        try {
            const chargeFrequencyValue = option.getAttribute('data-frequency') as string;
            const chargeFrequencyInput = this.querySelector('#chargeDelay') as HTMLInputElement;
            const chargeDelayExperimentInput = this.querySelector('#charge_delay_experiment') as HTMLInputElement;
            if (
                (!chargeFrequencyValue || chargeFrequencyValue == '0') &&
                chargeFrequencyInput
            ) {
                chargeFrequencyInput.value = '0';
                return;
            }
            let chargeDelay: number = 54;
            if (
                chargeFrequencyInput &&
                this.addToCartSubmit
            ) {
                chargeFrequencyInput.disabled = false;
                if (chargeFrequencyValue == '10' ) {
                    chargeDelay = 68;
                } else if (
                    chargeFrequencyValue != '8' &&
                    chargeFrequencyValue != '6'
                ) {
                    chargeDelay =  Number(chargeFrequencyValue) - 2;
                } else if (chargeFrequencyValue == '6') {
                    chargeDelay = 40;
                } else {
                    chargeDelay = 54;
                }
                /*
                    Charge Delay Experiemnt - Applies to two products only
                        - Marine Liquid Collagen Drink For Women, 14x10ml Sachets
                        - Marine Collagen Protein Supplement For Men, 14x10ml sachets
                */
                const isAbTestActive = this.productMain !== null
                    && this.productMain.hasAttribute('data-charge-delay-experiment-active');
                if (isAbTestActive) {
                    const chargeDelayExperimentEnabled = true;
                    if (chargeDelayExperimentEnabled) {
                        chargeDelay =
                        await this.chargeDelayExperiment(chargeDelayExperimentInput,
                            chargeDelay) || chargeDelay;
                    }
                }
                chargeFrequencyInput.value = chargeDelay.toString();
                if (this.addToCartSubmit) {
                    for (const button of this.addToCartSubmit) {
                        button.disabled = false;
                    }
                }
            }
        } catch (error) {
            throw new Error();
        }
    }
    async chargeDelayExperiment(chargeDelayExperimentInput: HTMLInputElement, chargeDelay: number) {
        const now = new Date();
        const dayOfWeek = now.getUTCDay();
        const hour = now.getHours();
        // Generate a random number between 1 - 10
        // If number is even then they will be included in the experiment
        const randomValue = Math.floor(Math.random() * 10) + 1;
        const partOfExperiment = randomValue % 2 === 0 ? true : false;
        // Check for weekdays and if before/after 16:00 pm.
        // If 4pm or later then it will add another day to chargeDelay
        if (dayOfWeek >= 1 && dayOfWeek <= 5 && hour >= 16 && partOfExperiment) {
            chargeDelay += 1;
        }
        chargeDelayExperimentInput.value = partOfExperiment.toString();
        if (this.addToCartSubmit) {
            for (const button of this.addToCartSubmit) {
                button.setAttribute('charge_delay_experiment', partOfExperiment.toString());
            }
        }
        return chargeDelay;
    }

    handleButtonAttributes() {
        // Populate button attributes on page load
        if (this.subscriptionChoices) {
            const selectedInput = Math.floor(this.subscriptionChoices.length / 2);
            const input = this.subscriptionChoices[selectedInput];
            if (this.addToCartSubmit && input) {
                const frequency = input?.id.split('-').pop() || '';
                const frequency_unit = input?.getAttribute('data-frequency-unit') || 'days';
                const sellingPlan = input?.dataset.sellingPlan || '';
                const quantity = input?.dataset.quantity || '';

                for (const button of this.addToCartSubmit) {
                    button?.setAttribute('is_subscription', 'true');
                    button?.setAttribute('quantity', quantity);
                    button?.setAttribute('selling_plan', sellingPlan);
                    button?.setAttribute('frequency', frequency);
                    button?.setAttribute('unit_type', frequency_unit);
                }
            }
        }
    }
}
